/**
 * Strip empty / whitespace-only headings (e.g. `<h2>&nbsp;</h2>`) out of
 * authored rich-text HTML before it is rendered via `v-html`.
 */
export function stripEmptyHeadings(html?: string | null): string {
  if (!html)
    return ''

  return html.replace(
    /<h([1-6])\b[^>]*>(?:\s|&nbsp;|&#160;|&#xa0;|<br\s*\/?>)*<\/h\1>/gi,
    '',
  )
}

/**
 * Convert any `<h1>` elements to `<h2>` elements in rich-text HTML.
 */
export function convertH1ToH2(html?: string | null): string {
  if (!html)
    return ''

  return html
    .replace(/<h1(\b[^>]*)>/gi, '<h2$1>')
    .replace(/<\/h1>/gi, '</h2>')
}

/**
 * Wrap each authored `<table>` in a horizontal scroll container so wide tables
 * scroll within their block instead of overflowing the page on narrow screens.
 */
export function wrapTablesForScroll(html?: string | null): string {
  if (!html)
    return ''

  return html
    .replace(/<table\b/gi, '<div class="overflow-x-auto" tabindex="0" role="region" aria-label="Scrollable table"><table')
    .replace(/<\/table>/gi, '</table></div>')
}

/**
 * Rebase authored heading levels so they run sequentially from `<h2>`, preserving
 * relative nesting. PIM content is authored per-field and cannot see the page `<h1>`.
 */
export function normalizeHeadingLevels(html?: string | null): string {
  if (!html)
    return ''

  let ancestors: number[] = []
  let current = 2

  return html.replace(/<(\/?)h([1-6])\b([^>]*)>/gi, (_match, closing: string, level: string, attrs: string) => {
    if (closing)
      return `</h${current}>`

    const authored = Number(level)
    ancestors = [...ancestors.filter(open => open < authored), authored]
    current = Math.min(ancestors.length + 1, 6)

    return `<h${current}${attrs}>`
  })
}
